import type { ToggleButtonGroupProps as MuiToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import {
  FormControl,
  FormHelperText,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
} from "@mui/material";
import React, { MouseEvent, ReactElement, ReactNode, useCallback } from "react";
import { SelectOption } from './types';

export type ToggleButtonGroupProps<T extends string | number, E> = {
  value: T | null | undefined;
  options: SelectOption<T, ReactNode, E>[] | null | undefined;
  onChange(e: MouseEvent<HTMLElement>, value: T | null, entity: E | null): void;
  error?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
  disableUnselect?: boolean;
  size?: MuiToggleButtonGroupProps['size'];
  color?: "standard" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
};

export const ToggleButtonGroup = <T extends string | number, E>(
  props: ToggleButtonGroupProps<T, E>
): ReactElement | null => {
  const {
    value,
    options,
    onChange,
    error,
    helperText,
    disabled,
    disableUnselect,
    size,
    color = "primary",
    margin = "normal",
    fullWidth = true,
    sx,
  } = props;

  const handleChange = useCallback(
    (e: MouseEvent<HTMLElement>, value: T | null) => {
      if (disableUnselect && value === null) {
        return;
      }

      const entity = options?.find((o) => o.value === value)?.entity as E;
      onChange(e, value, entity);
    },
    [disableUnselect, options, onChange]
  );

  return (
    <FormControl fullWidth={fullWidth} margin={margin} sx={sx}>
      <MuiToggleButtonGroup
        value={value}
        onChange={handleChange}
        disabled={disabled}
        size={size}
        color={color}
        fullWidth={fullWidth}
        exclusive
      >
        {options?.map((o, i) => (
          <ToggleButton value={o.value} key={i}>
            {o.title}
          </ToggleButton>
        ))}
      </MuiToggleButtonGroup>

      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default ToggleButtonGroup;
